import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {useIntl} from '@pinecast/i18n';
import styled, {focusVisibleRing, useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

import type {XAlign, YAlign} from './floating/placement';
import {
  alignmentFromPlacement,
  placementFromPreferences,
} from './floating/placement';
import {useMotionSafeTransitionStyles} from './floating/useMotionSafeTransitionStyles';
import {INTERACTIVE} from './helpers/tabbable';
import {ErrorIcon, Info} from './icons';
import ScreenReaderOnly from './ScreenReaderOnly';

type Size = 'normal' | 'large';

const maxWidths = {
  normal: 300,
  large: 500,
};

const TRANSITION_MS = 200;

// Matches the z-index `Layer` gives its portal, so tooltips keep stacking above
// page chrome now that they portal into a plain container of their own.
const LAYER_Z_INDEX = 100;

const TooltipWrapper = styled(
  'aside',
  ({
    $size,
    $xAlign,
    $yAlign,
  }: {
    $size: Size;
    $xAlign: XAlign;
    $yAlign: YAlign;
  }) => ({
    color: '#fff',
    display: 'inline-flex',
    maxWidth: maxWidths[$size],
    // The body is hoverable even though the positioning container around it is
    // not: WCAG 2.1 SC 1.4.13 requires that moving the pointer onto tooltip
    // content not dismiss it.
    pointerEvents: 'auto',
    position: 'relative',
    transformOrigin: `${$xAlign} ${$yAlign === 'top' ? 'bottom' : 'top'}`,
    zIndex: 3,

    [$yAlign === 'bottom' ? 'paddingTop' : 'paddingBottom']: 8,

    '::after': {
      border: '0 solid rgba(0, 0, 0, 0.8)',
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: 2,

      [$xAlign === 'left' ? 'left' : 'right']: 0,
      [$yAlign === 'bottom' ? 'top' : 'bottom']: 2,
      [$yAlign === 'bottom' ? 'borderBottom' : 'borderTop']:
        '6px solid rgba(0, 0, 0, 0.8)',
      [$xAlign === 'left' ? 'borderRight' : 'borderLeft']:
        '12px solid transparent',
    },
  }),
);

// A tab stop of its own unless `tabIndex` is -1, so it shows the ring when
// the keyboard reaches it. Outside it, as it wraps content of any color.
const DefaultWrapper = styled('span', (_, designColors) => ({
  display: 'inline-flex',
  ...focusVisibleRing(designColors),
}));

const defaultRenderWrapper = (props: Record<string, any>) => (
  <DefaultWrapper {...props} />
);

type Props = {
  active?: boolean;
  children?: React.ReactNode;
  positionerStyle?: React.CSSProperties;
  preferX?: XAlign;
  preferY?: YAlign;
  renderWrapper?: (props: Record<string, any>) => React.ReactElement | null;
  size?: Size;
  style?: React.CSSProperties;
  // Whether the wrapper is itself a tab stop. It has to be for the tooltip to
  // be reachable without a pointer (WCAG 2.1 SC 1.4.13, SC 2.1.1), so it
  // defaults to one. Pass `-1` when the wrapped content is already focusable —
  // a link or an enabled button — to avoid a duplicate tab stop; `onFocus`
  // bubbles up from the child, so the tooltip still opens on focus.
  //
  // Inside a control — a color chip drawn in a swatch button, say — the
  // wrapper is never a tab stop, whatever this says. The control takes focus,
  // and anything focusable inside it would be a control in a control (WCAG
  // 4.1.2). There the tooltip opens on hover.
  tabIndex?: number;
  tooltipContent: React.ReactNode;
  /**
   * What the tooltip is to the content it wraps, in the accessibility tree.
   *
   * - `'aux'` (default) — extra detail about the content. The wrapper points
   *   at the tooltip with `aria-describedby` while it is open.
   * - `'label'` — the content's name, for content that has none of its own
   *   (an icon, a swatch). The wrapper uses `aria-labelledby` instead, so the
   *   tooltip *replaces* the name rather than adding to it.
   * - `'none'` — no relationship, and the tooltip is hidden from assistive
   *   technology. For a wrapper that exposes the same text itself, which is
   *   the only way a screen reader on a phone gets it: the floating copy is
   *   in the DOM only while the tooltip is open, and is never announced as it
   *   appears. Pass it only for tooltips whose content is plain text —
   *   anything focusable inside one would be hidden with it.
   */
  type?: 'label' | 'aux' | 'none';
  offsetSkidding?: number;
  offsetDistance?: number;
};

const TooltipContainer = ({
  active = true,
  children,
  positionerStyle,
  preferX,
  preferY,
  renderWrapper,
  size = 'normal',
  style,
  tabIndex = 0,
  tooltipContent,
  type = 'aux',
  offsetSkidding,
  offsetDistance,
}: Props) => {
  const css = useCSS();

  const [open, setOpen] = React.useState(false);

  const {context, floatingStyles, placement, refs} = useFloating({
    open: open && active,
    onOpenChange: setOpen,
    placement: placementFromPreferences(preferX ?? 'left', preferY ?? 'bottom'),
    middleware: [
      // `alignmentAxis` reproduces the sign flip the old Popper `offset`
      // callback did by hand for `-end` placements.
      offset({
        mainAxis: offsetDistance ?? 0,
        alignmentAxis: offsetSkidding ?? 0,
      }),
      flip(),
      shift({padding: 8}),
    ],
    // Only attached while the tooltip is mounted, so the ResizeObserver and
    // scroll listeners go away when it closes.
    whileElementsMounted: autoUpdate,
  });

  // Whether the wrapper sits inside a control; see `tabIndex`. Nothing inside
  // a control may take focus. The wrapper is checked once it is in the
  // document, which is before the first paint.
  const [insideControl, setInsideControl] = React.useState(false);
  const setReference = React.useCallback(
    (node: Element | null) => {
      refs.setReference(node);
      setInsideControl(Boolean(node?.parentElement?.closest(INTERACTIVE)));
    },
    [refs],
  );

  const hover = useHover(context, {
    enabled: active,
    move: false,
    // Touch belongs to `useClick` alone. A tap also emits `pointerenter`, and
    // then the compatibility `mouseleave` — so without this, a phone opened
    // the tooltip on hover and closed it again in the same tap.
    mouseOnly: true,
    // Lets the pointer travel from the anchor onto the tooltip without it
    // vanishing en route.
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, {enabled: active});
  // Mouse is left to `useHover`; this covers touch taps and keyboard
  // activation, which is the only way to reach the content on a phone.
  const click = useClick(context, {
    enabled: active,
    ignoreMouse: true,
    // A second tap closes the tooltip again. `stickIfOpen` (on by default)
    // keeps Enter from closing one the keyboard has just opened on focus,
    // because that tooltip was not opened by a click.
    toggle: true,
  });
  const dismiss = useDismiss(context, {enabled: active});
  const role = useRole(context, {
    enabled: active && type !== 'none',
    role: type === 'label' ? 'label' : 'tooltip',
  });

  const {getFloatingProps, getReferenceProps} = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  // `open` is spelled out rather than left to default, which would return `''`
  // for each `initial` key and lean on empty inline styles to fall back to the
  // class.
  const {isMounted, styles: transitionStyles} = useMotionSafeTransitionStyles(
    context,
    {
      duration: TRANSITION_MS,
      initial: {opacity: 0, transform: 'scale(0.9)'},
      open: {opacity: 1, transform: 'scale(1)'},
    },
  );

  const {xAlign, yAlign} = alignmentFromPlacement(placement);

  return (
    <>
      {/* Two references, deliberately. The wrapper is the *interaction*
          reference, because it carries the handlers and the tab stop.
          `useDismiss` counts a press outside that element as a press outside
          the tooltip, so a wrapper wider than its content — a table cell, or
          an icon button with a padded hit area — would close the tooltip and
          let the same press reopen it. The inner span stays the *position*
          reference, so the tooltip still points at the content. */}
      {(renderWrapper || defaultRenderWrapper)({
        ...getReferenceProps(),
        ref: setReference,
        style,
        tabIndex: insideControl ? undefined : tabIndex,
        children: (
          <span
            ref={refs.setPositionReference}
            style={{
              display: 'inline-flex',
              verticalAlign: 'middle',
              ...positionerStyle,
            }}
          >
            {children}
          </span>
        ),
      })}
      {isMounted && (
        <FloatingPortal>
          <div
            className={css({
              display: 'flex',
              justifyContent: xAlign === 'left' ? 'flex-start' : 'flex-end',
              // The container is as wide as the widest the tooltip may get, so
              // it must not swallow pointer events aimed at the page beneath.
              pointerEvents: 'none',
              width: `${maxWidths[size]}px`,
              zIndex: LAYER_Z_INDEX,
            })}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            aria-hidden={type === 'none' || undefined}
          >
            <TooltipWrapper
              $size={size}
              $xAlign={xAlign}
              $yAlign={yAlign}
              __forceStyle={transitionStyles}
            >
              <div
                className={css({
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  borderBottomLeftRadius: '3px',
                  borderBottomRightRadius: '3px',
                  borderTopLeftRadius: '3px',
                  borderTopRightRadius: '3px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 0,
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '8px',
                  paddingLeft: '8px',
                  zIndex: 2,
                  [`border${yAlign === 'bottom' ? 'Top' : 'Bottom'}${
                    xAlign === 'left' ? 'Left' : 'Right'
                  }Radius`]: 0,
                })}
              >
                {tooltipContent}
              </div>
            </TooltipWrapper>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default TooltipContainer;

/**
 * The trigger for an icon whose tooltip *is* its content.
 *
 * A real `button`, not a `span` with `role="button"`: a `label` hands a click
 * to its field unless the click lands on interactive content, and a `tabindex`
 * does not make an element interactive content.
 */
const IconTooltipButton = styled(
  'button',
  (_, designColors) => ({
    alignItems: 'center',
    appearance: 'none',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 3,
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    justifyContent: 'center',
    lineHeight: '1',
    margin: 0,
    padding: 0,
    // Anchors the hit area below and the description, both out of flow.
    position: 'relative',

    // The glyph is 14px square, too small to hit on a phone. A pseudo-element
    // grows the pointer target to 24px (WCAG 2.1 SC 2.5.8), which leaves the
    // icon its own size in the line of text it sits in.
    '::after': {
      content: '""',
      height: 24,
      left: '50%',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: 24,
    },

    ...focusVisibleRing(designColors),
  }),
  {type: 'button'},
);

/**
 * A help or warning icon whose only content is its tooltip.
 *
 * The text is in the DOM twice: the floating copy, and a visually hidden copy
 * that is always there for `aria-describedby` to point at. `TooltipContainer`'s
 * `type` prop covers why the hidden copy is the one a screen reader reads.
 */
const IconTooltip = ({
  children,
  icon,
  label,
  size,
  style,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  label: string;
  size?: Size;
  style?: React.CSSProperties;
}) => {
  const descriptionId = React.useId();
  // `tabIndex` is dropped: a `button` is already a tab stop, and spelling the
  // attribute out would only make the icon look focusable for a reason other
  // than what it is.
  const renderWrapper = ({
    children: wrapped,
    tabIndex: _tabIndex,
    ...wrapperProps
  }: Record<string, any>) => (
    <IconTooltipButton
      {...wrapperProps}
      aria-describedby={descriptionId}
      aria-label={label}
    >
      {wrapped}
      <ScreenReaderOnly id={descriptionId}>{children}</ScreenReaderOnly>
    </IconTooltipButton>
  );

  return (
    <TooltipContainer
      renderWrapper={renderWrapper}
      size={size}
      style={style}
      tooltipContent={children}
      type="none"
      offsetDistance={8}
    >
      {icon}
    </TooltipContainer>
  );
};

export const HelpIcon = (props: {
  children: React.ReactNode;
  iconSize?: number;
  size?: Size;
  style?: React.CSSProperties;
}) => {
  const designColors = useColors();
  const {formatMessage} = useIntl();
  return (
    <IconTooltip
      icon={
        <Info color={designColors.headingBlue} size={props.iconSize || 14} />
      }
      label={formatMessage({
        description: 'Accessibility label for an icon that reveals help text',
        defaultMessage: 'More information',
      })}
      size={props.size}
      style={props.style}
    >
      {props.children}
    </IconTooltip>
  );
};

export const WarningIcon = (props: {
  children: React.ReactNode;
  iconSize?: number;
  size?: Size;
  style?: React.CSSProperties;
}) => {
  const designColors = useColors();
  const {formatMessage} = useIntl();
  return (
    <IconTooltip
      icon={
        <ErrorIcon
          color={designColors.borderRedAccent}
          size={props.iconSize || 14}
        />
      }
      label={formatMessage({
        description: 'Accessibility label for an icon that reveals a warning',
        defaultMessage: 'Warning',
      })}
      size={props.size}
      style={props.style}
    >
      {props.children}
    </IconTooltip>
  );
};
